
import { supabase } from "@/integrations/supabase/client";
import { AdItem } from "./types";

// Cache expiration (6 hours)
export const CACHE_EXPIRATION = 6 * 60 * 60 * 1000;
// Refresh interval (60 minutes)
export const REFRESH_INTERVAL = 60 * 60 * 1000;

/**
 * Get cached ad data from Supabase
 */
export async function getAdDataFromCache(
  userId: string,
  campaignId: string
): Promise<{ data: AdItem[] | null; timestamp: number } | null> {
  try {
    const { data: cachedData, error: cacheError } = await supabase
      .from('meta_ads_cache')
      .select('*')
      .eq('campaign_id', campaignId)
      .eq('user_id', userId)
      .gt('expires_at', new Date().toISOString())
      .single();
      
    if (cachedData && !cacheError) {
      console.log("Using cached ads data from Supabase", cachedData.fetched_at);
      
      // Parse the JSON data if it's stored as a string
      // or use as-is if it's already an object
      const adsData = typeof cachedData.ads_data === 'string' 
        ? JSON.parse(cachedData.ads_data) 
        : cachedData.ads_data;
        
      return {
        data: adsData as AdItem[],
        timestamp: new Date(cachedData.fetched_at).getTime()
      };
    }
  } catch (cacheError) {
    console.log("Error checking cache:", cacheError);
  }
  
  return null;
}

/**
 * Save ad data to Supabase cache
 */
export async function saveAdDataToCache(
  userId: string,
  campaignId: string, 
  adsData: AdItem[],
  isSampleData: boolean = false
): Promise<void> {
  try {
    const now = Date.now();
    const { error: insertError } = await supabase
      .from('meta_ads_cache')
      .upsert({
        user_id: userId,
        campaign_id: campaignId,
        ads_data: adsData as any, // Using type assertion to fix TypeScript error
        fetched_at: new Date().toISOString(),
        expires_at: new Date(now + CACHE_EXPIRATION).toISOString(),
        is_sample_data: isSampleData
      }, {
        onConflict: 'user_id,campaign_id'
      });
      
    if (insertError) {
      console.error("Error saving to cache:", insertError);
    } else {
      console.log("Successfully saved ads data to cache");
    }
  } catch (dbError) {
    console.error("Database error when saving data:", dbError);
  }
}

/**
 * Clean expired cache entries
 */
export async function cleanExpiredCache(): Promise<void> {
  try {
    await supabase.rpc('clean_expired_meta_cache');
    console.log("Cleaned expired cache entries");
  } catch (error) {
    console.error("Failed to clean cache:", error);
  }
}
